import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../api";
import { RootState } from "../index";
import { User } from "../../types/user";
// import { CreateUserPayload } from "../../types/User";

interface CreateUserPayload {
    email: string;
    firstName: string;
    lastName: string;
    phone_number: string;
    practice: any;
    role: string;
    cognito_user_id: string;
}

interface UserState {
    user: User | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: UserState = { 
    user: null,
    isLoading: false,
    error: null,
};

export const createUser = createAsyncThunk<User, CreateUserPayload>(
    'users/createUser',
    async (userData, { rejectWithValue }) => {
        try {
            // Try the root endpoint first (since that's what works in API Gateway)
            const response = await api.post('/', userData);
            
            // Handle the actual Lambda response structure
            if (response.data.success) {
                return response.data.data; // Lambda returns data in response.data.data
            } else {
                throw new Error(response.data.error || 'User creation failed');
            }
        } catch (error: any) {
            console.error('❌ API Error:', error);
            console.error('📄 Error response:', error.response?.data);
            
            // Handle different error response structures
            const errorMessage = error.response?.data?.error || 
                                error.response?.data?.message || 
                                error.message || 
                                'Failed to create user';
            return rejectWithValue(errorMessage);
        }
    }
);


const setPending = (state: UserState) => {
    state.isLoading = true;
    state.error = null;
};

const setError = (state: UserState, action: PayloadAction<unknown>) => {
    state.isLoading = false;
    state.error = action.payload as string;
};

const setUser = (state: UserState, action: PayloadAction<User>) => {
    state.isLoading = false;
    state.user = action.payload;
};

const userSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(createUser.pending, setPending);
        builder.addCase(createUser.fulfilled, setUser);
        builder.addCase(createUser.rejected, setError);
    },
});

export const selectUser = (state: RootState) => state.user.user;
export const selectUserLoadingStatus = (state: RootState) => ({
    isLoading: state.user.isLoading,
    error: state.user.error,
});

export default userSlice.reducer;
